import Vue from "vue";
import { Store } from "vuex";
import VueRouter from "vue-router";
import { Inject, Service } from "typedi";
import { AxiosError } from "axios";
import { DefaultErrorResponseBody } from "@flosum-auth/contracts";
import { INVALID_SESSION_EXCEPTION, INVALID_SESSION_EXCEPTION_STATUS } from "@/modules/auth/auth.constants";
import { HttpInterceptor } from "~shared/classes/http-interceptor";
import { CoreSymbols } from "@/core/core.symbols";
import { AbortResponseError } from "~shared/errors/abort-response.error";
import { AuthStoreDefinition } from "@/modules/auth/store/auth.definitions";

@Service()
export class InvalidSessionInterceptor extends HttpInterceptor {
  private static _redirectingState = false;

  constructor(
    @Inject(CoreSymbols.router) private readonly router: VueRouter,
    @Inject(CoreSymbols.store) private readonly store: Store<AuthStoreDefinition>
  ) {
    super();
  }

  public async onResponseError(error: AxiosError<DefaultErrorResponseBody>): Promise<void> {
    if (
      !error.response ||
      error.response.status !== INVALID_SESSION_EXCEPTION_STATUS ||
      error.response.data?.message !== INVALID_SESSION_EXCEPTION
    ) {
      throw error;
    }

    if (
      this.router.currentRoute.matched.some((route) => !!route.name?.match(/^Auth.+/)) ||
      InvalidSessionInterceptor._redirectingState
    ) {
      throw new AbortResponseError();
    }

    InvalidSessionInterceptor._redirectingState = true;

    const query = {
      redirectTo: this.router.currentRoute.fullPath,
    };

    if (this.store.state.auth.user) {
      await Vue.notify({
        type: "info",
        text: "Your session has expired",
        duration: 2000,
      });
      await this.store.dispatch("auth/logout");
    }

    await this.router.push({
      name: "AuthLogin",
      query,
    });

    InvalidSessionInterceptor._redirectingState = false;

    throw new AbortResponseError();
  }
}
